import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  GoalCardBottomContainer,
  GoalCardContainer,
  MoreButtonContainer,
  GoalCardDescription,
  GoalCardTextarea,
  DiscardButton,
  CancellIconLarge,
  GoalProgressCommentLabel,
  GoalProgressComment,
  MobileActionsContainer,
  CoalBadge,
} from './style/GoalCard.styled';
import {
  MoreCircle16Regular as PasteIcon,
  MoreHorizontal16Filled as MoreIcon,
  Play16Regular as PlayIcon,
  Checkmark16Regular as CheckmarkIcon,
  ShiftsActivity16Regular as ShiftsActivityIcon,
  ShiftsActivity20Regular as ShiftsActivityIconLarge,
  Add16Regular as AddIcon,
} from '@fluentui/react-icons';

import { Field, Label, PositioningShorthand } from '@fluentui/react-components';
import {
  EmployeeGoal,
  GoalStatus,
  StatusDisplayText,
} from 'src/tabs/models/GoalsInterfaces';
import { CancellIcon } from './style/GoalCard.styled';
import { PrimaryButton } from 'src/styles/globalStyles/buttonStyles.styled';
import { PopoverTrigger, Popover } from '@fluentui/react-components';
import { useUpdateGoals } from 'src/tabs/hooks/useUpdateGoals';
import { appearance } from 'src/hooks/useAlertMessage';
import { createPortal } from 'react-dom';
import { FocusBackground } from '../GoalsTable/style/GoalsTable.styled';
import { isMobile } from 'react-device-detect';
import { ActionsPopup } from './ActionsPopup/ActionsPopup';
import {
  GOAL_CARD_SUCCESS_MESSAGE,
  GoalsActions,
} from 'src/tabs/constants/Goals.constants';

const DisplayIconsByStatus = {
  [GoalStatus.DRAFT]: <PasteIcon />,
  [GoalStatus.APPROVED]: <PlayIcon />,
  [GoalStatus.IN_PROGRESS]: <CheckmarkIcon />,
  [GoalStatus.COMPLETED]: <CheckmarkIcon />,
  [GoalStatus.CANCELLED]: <CancellIcon />,
  [GoalStatus.INACTIVE_CANCELLED]: <CancellIcon />,
  [GoalStatus.INACTIVE_COMPLETED]: <CheckmarkIcon />,
  [GoalStatus.DEFFERED]: <ShiftsActivityIcon />,
};

const HIDE_MENU_STATUSES = [
  GoalStatus.COMPLETED,
  GoalStatus.INACTIVE_COMPLETED,
];

interface GoalCardProps {
  goal: EmployeeGoal;
  status: GoalStatus;
  rowName: string;
  selectedPeriod: string;
  isAdding?: boolean;
  isEditing?: boolean;
  visible?: boolean;
  discardAdding?: () => void;
  setIsEditingCard?: () => void;
  discardEditingCard?: () => void;
  showAlert?: (message: string, appearance: appearance) => void;
  getAllUserGoals: () => void;
  isReadOnlyPeriod: boolean;
  target?: HTMLElement;
  scrollToCard?: (top: number, isVisible: boolean) => void;
}

const HIDE_CANCEL_BUTTON_STATUSES = [
  GoalStatus.CANCELLED,
  GoalStatus.INACTIVE_CANCELLED,
];

export const GoalCard = ({
  goal,
  status,
  rowName,
  isAdding = false,
  isEditing = false,
  visible = true,
  discardAdding,
  setIsEditingCard,
  discardEditingCard,
  selectedPeriod,
  getAllUserGoals,
  showAlert,
  isReadOnlyPeriod = false,
  target = null,
  scrollToCard,
}: GoalCardProps) => {
  const initialTextAreaValue = isAdding
    ? ''
    : rowName === StatusDisplayText.DRAFT
      ? goal?.cra0e_description
      : goal?.cra0e_progresscomment || '';
  const [startProgressActive, setStartProgressActive] = useState(false);
  const [editProgressComment, setEditProgressComment] = useState(false);
  const [startProgressComplete, setStartProgressComplete] = useState(false);
  const [isCancelingGoal, setIsCancelingGoal] = useState(false);
  const [isDefferingGoal, setIsDefferingGoal] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [goalTextFieldValue, setGoalTextFieldValue] =
    useState(initialTextAreaValue);
  const { updateUserGoal, loadingUpdateGoal, postUserGoal } =
    useUpdateGoals(getAllUserGoals);
  const [textAreaError, setTextAreaError] = useState('');
  const [windowHeight] = useState(window.innerHeight);
  const [, setKeyboardHeight] = useState(0);

  const positioning: PositioningShorthand = isMobile
    ? { position: 'below', align: 'start', target }
    : 'below-start';

  const goalCardRef = useRef<HTMLDivElement>(null);

  const badgeClickHandler = () => {
    if (rowName === StatusDisplayText.APPROVED) {
      setStartProgressActive(true);
    }
    if (rowName === StatusDisplayText.IN_PROGRESS) {
      setStartProgressComplete(true);
    }
  };

  const resizeTextField = (
    e: React.FocusEvent<HTMLTextAreaElement, Element>
  ) => {
    const element = e.target;
    if (!element?.scrollHeight) return;
    element.style.height = element.scrollHeight + 'px';
    e.currentTarget.setSelectionRange(
      e.currentTarget.value.length,
      e.currentTarget.value.length
    );
  };

  const discardClickHandler = () => {
    if (isEditing) {
      discardEditingCard();
    }
    if (isAdding) {
      discardAdding();
    }
    if (editProgressComment) {
      setEditProgressComment(false);
    }
    if (startProgressComplete) {
      setStartProgressComplete(false);
    }
    if (startProgressActive) {
      setStartProgressActive(false);
    }
    if (isCancelingGoal) {
      setIsCancelingGoal(false);
    }
    if (isDefferingGoal) {
      setIsDefferingGoal(false);
    }
    setGoalTextFieldValue(initialTextAreaValue);
  };

  const inputsVisible = isAdding || isEditing;
  const inputsNotVisible = !isEditing && !isAdding;
  const goalStatus = GoalStatus[status];
  const displayStatus =
    rowName === StatusDisplayText.DRAFT || rowName === StatusDisplayText.CLOSED;
  const statusText = displayStatus
    ? StatusDisplayText[goalStatus]
    : rowName === StatusDisplayText.APPROVED
      ? 'Start progress'
      : 'Mark as complete';
  const badgeColor = displayStatus ? 'tint' : 'ghost';
  const badgeShowConditions =
    inputsNotVisible &&
    !startProgressActive &&
    !editProgressComment &&
    !startProgressComplete &&
    !isCancelingGoal &&
    !isDefferingGoal;

  const textAreaShowConditions =
    isAdding ||
    isEditing ||
    editProgressComment ||
    startProgressActive ||
    startProgressComplete ||
    isCancelingGoal ||
    isDefferingGoal;

  const bottomActionsShowConditions = !textAreaShowConditions;
  const textAreaPlaceholderText =
    rowName === StatusDisplayText.DRAFT && !isCancelingGoal
      ? 'Please briefly describe your goal.'
      : 'Please submit your progress comment to continue.';

  const saveButtonText =
    editProgressComment || isEditing ? (
      'Save'
    ) : isAdding ? (
      <>
        <AddIcon />
        Add Goal
      </>
    ) : startProgressActive ? (
      <>
        <PlayIcon />
        &nbsp;Start
      </>
    ) : isCancelingGoal ? (
      <>
        <CancellIconLarge />
        &nbsp;Cancel
      </>
    ) : isDefferingGoal ? (
      <>
        <ShiftsActivityIconLarge />
        &nbsp;Defer
      </>
    ) : (
      <>
        <CheckmarkIcon />
        &nbsp;Complete
      </>
    );

  const handleSubmit = async () => {
    if (!goalTextFieldValue && textAreaShowConditions) {
      setTextAreaError('This is a required field.');
      return;
    } else {
      setTextAreaError('');
    }
    if (isAdding) {
      await postUserGoal({
        cra0e_description: goalTextFieldValue,
        new_period: {
          new_axsperiodid: selectedPeriod,
        },
      });
      showAlert(GOAL_CARD_SUCCESS_MESSAGE, 'success');
      discardAdding();
    }
    if (isEditing) {
      await updateUserGoal({
        cra0e_description: goalTextFieldValue,
        cra0e_employeegoalid: goal.cra0e_employeegoalid,
      });
      discardEditingCard();
    }
    if (startProgressActive) {
      await updateUserGoal({
        cra0e_progresscomment: goalTextFieldValue,
        cra0e_employeegoalid: goal.cra0e_employeegoalid,
        statuscode: GoalStatus.IN_PROGRESS,
      });
    }
    if (editProgressComment) {
      await updateUserGoal({
        cra0e_progresscomment: goalTextFieldValue,
        cra0e_employeegoalid: goal.cra0e_employeegoalid,
        statuscode: GoalStatus.IN_PROGRESS,
      });
    }
    if (startProgressComplete) {
      await updateUserGoal({
        cra0e_progresscomment: goalTextFieldValue,
        cra0e_employeegoalid: goal.cra0e_employeegoalid,
        statuscode: GoalStatus.COMPLETED,
      });
    }
    if (isCancelingGoal) {
      await updateUserGoal({
        cra0e_progresscomment: goalTextFieldValue,
        cra0e_employeegoalid: goal.cra0e_employeegoalid,
        statuscode: GoalStatus.CANCELLED,
      });
    }
    if (isDefferingGoal) {
      await updateUserGoal({
        cra0e_progresscomment: goalTextFieldValue,
        cra0e_employeegoalid: goal.cra0e_employeegoalid,
        statuscode: GoalStatus.DEFFERED,
      });
    }
  };

  const restoreGoal = async () => {
    updateUserGoal({
      cra0e_progresscomment: '',
      cra0e_employeegoalid: goal.cra0e_employeegoalid,
      statuscode: GoalStatus.DRAFT,
    });
  };

  useEffect(() => {
    const handleResize = (e) => {
      setKeyboardHeight(e.target.height);
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    };
    visualViewport.addEventListener('resize', handleResize);
    return () => visualViewport.removeEventListener('resize', handleResize);
  }, [windowHeight]);

  useEffect(() => {
    if (isMobile) {
      document.documentElement.style.overflow = 'hidden'; // html
      document.body.style.overflow = 'hidden'; // body
    }
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    const goalCardDimensions = goalCardRef.current.getBoundingClientRect();
    const isVisible =
      goalCardDimensions.top >= 0 &&
      goalCardDimensions.bottom <= window.innerHeight;
    scrollToCard(goalCardRef.current.offsetTop, isVisible);
  }, [isEditing, goalCardRef.current]);

  const handleGoalTextAreaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (isAdding || isEditing) {
      const element = e.target;

      element.style.height = '0px';

      const scrollHeight = element.scrollHeight;

      element.style.height = `${scrollHeight}px`;
    }

    setGoalTextFieldValue(e.target.value);
  };

  const showCancelButton = !HIDE_CANCEL_BUTTON_STATUSES.includes(status);

  const showGoalMenu = !HIDE_MENU_STATUSES.includes(status);

  return (
    <GoalCardContainer
      visible={visible}
      overlay={
        isEditing ||
        isAdding ||
        isCancelingGoal ||
        isDefferingGoal ||
        startProgressActive ||
        startProgressComplete
      }
      hasInput={textAreaShowConditions}
      position={0}
      isMobile={isMobile}
      ref={goalCardRef}
    >
      {isMobile && textAreaShowConditions && !isReadOnlyPeriod && (
        <MobileActionsContainer>
          <CancellIconLarge onClick={discardClickHandler} />
          <h1>
            {isAdding
              ? GoalsActions.ADD
              : isEditing
                ? GoalsActions.EDIT
                : isDefferingGoal
                  ? GoalsActions.DEFER
                  : isCancelingGoal
                    ? GoalsActions.CANCEL
                    : startProgressActive
                      ? GoalsActions.START
                      : startProgressComplete
                        ? GoalsActions.COMPLETE
                        : ''}
          </h1>
          <p
            onClick={() => {
              if (!loadingUpdateGoal) {
                handleSubmit();
              }
            }}
          >
            Save
          </p>
        </MobileActionsContainer>
      )}
      {createPortal(
        isEditing ? <FocusBackground onClick={discardClickHandler} /> : <></>,
        document.getElementById('overlays')
      )}
      {createPortal(
        isAdding ? <FocusBackground onClick={discardClickHandler} /> : <></>,
        document.getElementById('overlays')
      )}
      {isMobile &&
        (isCancelingGoal ||
          isDefferingGoal ||
          startProgressActive ||
          startProgressComplete) &&
        createPortal(
          <FocusBackground onClick={discardClickHandler} />,
          document.getElementById('overlays')
        )}

      {inputsNotVisible && (
        <GoalCardDescription>{goal.cra0e_description}</GoalCardDescription>
      )}
      {inputsVisible && (
        <Label required>{isAdding ? 'Goal' : GoalsActions.EDIT}</Label>
      )}
      {goal?.cra0e_progresscomment && !textAreaShowConditions && (
        <div>
          <GoalProgressCommentLabel>Progress comment</GoalProgressCommentLabel>
          <GoalProgressComment>
            {goal?.cra0e_progresscomment}
          </GoalProgressComment>
          {rowName === StatusDisplayText.CLOSED && goal?.cra0e_evaluation && (
            <GoalProgressCommentLabel>Evaluation</GoalProgressCommentLabel>
          )}
          {rowName === StatusDisplayText.CLOSED && goal?.cra0e_evaluation && (
            <GoalProgressComment>{goal?.cra0e_evaluation}</GoalProgressComment>
          )}
        </div>
      )}
      {textAreaShowConditions && inputsNotVisible && (
        <Label required>Progress comment</Label>
      )}
      {textAreaShowConditions && (
        <Field
          validationMessage={textAreaShowConditions && textAreaError}
          required
        >
          <GoalCardTextarea
            placeholder={textAreaPlaceholderText}
            shown={textAreaShowConditions}
            onChange={(e) => handleGoalTextAreaChange(e)}
            onFocus={(e) => resizeTextField(e)}
            value={goalTextFieldValue}
            autoFocus={true}
          />
        </Field>
      )}
      <GoalCardBottomContainer>
        {badgeShowConditions && (
          <CoalBadge
            size="extra-large"
            badgeColor={badgeColor}
            appearance={badgeColor}
            icon={DisplayIconsByStatus[status]}
            onClick={badgeClickHandler}
          >
            {statusText}
          </CoalBadge>
        )}
        <MoreButtonContainer>
          {inputsNotVisible && (
            <Popover
              positioning={positioning}
              open={isMenuVisible}
              onOpenChange={(e, { open }) => setIsMenuVisible(open)}
            >
              {bottomActionsShowConditions && showGoalMenu && (
                <PopoverTrigger disableButtonEnhancement>
                  <MoreIcon />
                </PopoverTrigger>
              )}

              <ActionsPopup
                isMenuVisible={isMenuVisible}
                setIsMenuVisible={setIsMenuVisible}
                bottomActionsShowConditions={bottomActionsShowConditions}
                showGoalMenu={showGoalMenu}
                rowName={rowName}
                setIsDefferingGoal={setIsDefferingGoal}
                setIsEditingCard={setIsEditingCard}
                setEditProgressComment={setEditProgressComment}
                showCancelButton={showCancelButton}
                setIsCancelingGoal={setIsCancelingGoal}
                setGoalTextFieldValue={setGoalTextFieldValue}
                goal={goal}
                restoreGoal={restoreGoal}
              />
            </Popover>
          )}
          {!isMobile && textAreaShowConditions && (
            <DiscardButton appearance="secondary" onClick={discardClickHandler}>
              Discard
            </DiscardButton>
          )}
          {!isMobile && textAreaShowConditions && (
            <PrimaryButton onClick={handleSubmit} disabled={loadingUpdateGoal}>
              {saveButtonText}
            </PrimaryButton>
          )}
        </MoreButtonContainer>
      </GoalCardBottomContainer>
    </GoalCardContainer>
  );
};
